import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Globe2, LineChart, Database, Network, Cpu, CheckCircle2, Shield, FileSearch } from "lucide-react";
import { PageHeader, CTABand } from "./Services";

const WHO = [
  { icon: Building2, t: "Operators", d: "iGaming, sports betting and entertainment brands." },
  { icon: Network, t: "Affiliate partners", d: "Curated publishers, networks and media buyers." },
  { icon: LineChart, t: "Media buyers", d: "Performance teams running paid acquisition." },
  { icon: Globe2, t: "Lead generation partners", d: "Specialists in qualified audience supply." },
  { icon: Database, t: "Data providers", d: "Owners and licensors of compliant audience data." },
  { icon: Cpu, t: "Technology providers", d: "Tracking, attribution, CRM and BI vendors." },
  { icon: ArrowUpRight, t: "Strategic commercial partners", d: "Long-term commercial partners and platforms." },
];

const VALUES = [
  "Quality traffic",
  "Transparent sourcing",
  "Documented rights",
  "Compliance awareness",
  "Performance accountability",
  "Long term commercial relationships",
];

const ONBOARD = [
  { icon: FileSearch, t: "Traffic source review", d: "We review the channels, properties and creative used by the partner." },
  { icon: Shield, t: "Data rights and consent", d: "We check the basis on which audiences are activated, including consent where required." },
  { icon: LineChart, t: "Campaign history", d: "We look at prior performance, complaint rates and partner references where appropriate." },
  { icon: CheckCircle2, t: "Activation and review", d: "Once approved, we set expectations, monitor signals and review on a regular cadence." },
];

export default function Partners() {
  React.useEffect(() => {
    document.title = "Partner With PlayerReach";
  }, []);
  return (
    <div data-testid="partners-page">
      <PageHeader
        eyebrow="Partners"
        title="A partner network built on traffic quality, transparent sourcing and documented rights."
        subtitle="We work with the operators, affiliates and providers who treat acquisition as a long-term, accountable discipline — not a one-off send."
      />

      {/* WHO */}
      <section className="section-pad">
        <div className="container-pr">
          <p className="overline">Who we work with</p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight max-w-2xl">A curated commercial network across the acquisition stack.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHO.map((w, i) => (
              <div key={w.t} className="card-flat p-6" data-testid={`who-${i}`}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-50 border border-slate-200 text-[#0052FF]">
                  <w.icon className="h-5 w-5" />
                </span>
                <p className="font-display mt-4 font-semibold text-slate-900">{w.t}</p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-slate-50 border-y border-slate-200 section-pad">
        <div className="container-pr">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="overline">What we value</p>
              <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Six values, applied to every partner relationship.</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">Our commercial standards are documented and applied consistently. They are how we maintain trust with operators, regulators and recipients across every market we serve.</p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {VALUES.map((v) => (
                <div key={v} className="flex items-center gap-3 card-flat p-4">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ONBOARDING */}
      <section className="section-pad">
        <div className="container-pr">
          <p className="overline">Partner onboarding</p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight max-w-3xl">We review before we activate, and we keep reviewing after.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl">PlayerReach may review traffic sources, data rights, consent records, collection methods, campaign history, complaint rates and compliance processes before activating a relationship. Reviews continue after activation, on a cadence proportionate to the channel.</p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {ONBOARD.map((s, i) => (
              <div key={s.t} className="relative card-flat p-6" data-testid={`onboard-${i}`}>
                <span className="absolute -top-3 left-6 inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#0052FF] text-white text-xs font-semibold">0{i + 1}</span>
                <s.icon className="h-5 w-5 text-cyan-600 mt-2" />
                <p className="font-display mt-4 font-semibold text-slate-900">{s.t}</p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="partner-cta">
              Become a Partner <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/legal/acceptable-traffic-sources-policy" className="btn-secondary" data-testid="partner-traffic-policy">
              Read traffic policy <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
